<template lang="pug">
.consultation-details
  template(v-if="isFetching")
    v-container.pa-2(fluid)
      v-row(no-gutters)
        v-col
          v-row
            v-col(cols="1")
              v-btn(icon @click="onClickBack")
                v-icon(color="primary") mdi-arrow-left
            v-col
              v-skeleton-loader(type="article@5")
        v-col
          v-skeleton-loader(type="article@5")
  template(v-else)
    .main(:style="{ '--view-height': viewHeight }")
      v-row(no-gutters)
        v-col(:cols="showRightPanel ? 8 : 12")
          .middle-content
            .pa-4
              v-sheet.d-flex.align-center.yellow100.round-xl.pa-3.mb-4(v-if="hasReservationsOrPreorders" style="border: 1px solid #FC0 !important;")
                v-icon(size="20" color="danger500") $yzi-info-circle
                div.ml-3.flex-grow-1
                  div.yz-regular-body-md {{ reservationPreorderBannerText }}
                y-button(label="Learn more" size="xs" button-class="px-3" height="28" @click="$emit('showUpgradeDialog')")
              v-row.profile-height
                v-col(cols="5")
                  .d-flex
                    v-btn.mr-4(icon small @click="onClickBack")
                      v-icon(color="primary") mdi-arrow-left
                    .mr-4
                      v-img.round-xl(v-if="patient.profile_image" width="60" height="80" :src="patient.profile_image.file" cover)
                        template(v-slot:placeholder)
                          v-row(class="fill-height ma-0" align="center" justify="center")
                            v-progress-circular(indeterminate color="grey")
                      v-card.neutral300.fill-height.round-xl(v-else flat width="60" height="80")
                        div.d-flex.align-center.justify-center.fill-height
                          v-icon(size="30") $yzi-user-03
                    div
                      .text-lg.font-semibold.mb-1.clickable.hover-text(@click="openProfileWindow") {{ patient.name }}
                      div.text-xs.font-medium {{ patient.ic }}
                      div.text-xs.font-medium(v-if="patient.dob") {{ $options.filters.getAgeByYearMonthDay(patient.dob) | displayAgeByYearMonthDay }} old
                      div.text-xs.text-capitalize.font-medium {{ patient.gender }}
                      //- div.text-xs.font-medium(v-if="patient.phone") +{{ patient.phone }}
                v-divider(vertical inset)
                v-col
                  .d-flex
                    .text-xs.flex-grow-1
                      .font-semibold.mb-1 {{ $t('allergy') }}
                      template(v-for="allergy in consultation.allergies")
                        .mb-1
                          div {{ allergy.name }}
                          .text-xxs.italic {{ allergy.reaction }}
                    div
                      v-btn(@click="showEditAllergyDialog = true" icon small)
                        v-icon(color="primary" size=16) $yzi-edit-05
                v-divider(vertical inset)
                v-col
                  .d-flex
                    .text-xs.flex-grow-1
                      .font-semibold.mb-1 {{ $t('payment') }}
                      //- Panel
                      div(v-if="consultation.registration.default_payment.panel")
                        .mb-1 {{ consultation.registration.default_payment.panel.name }}
                        //- For remark type
                        template(v-if="isPanelRemarkType")
                          v-tooltip(v-if="consultation.registration.default_payment.panel.remarks" bottom color="black" content-class="black tooltip-bottom-arrow")
                            template(#activator="{ on, attrs }")
                              div(v-bind="attrs" v-on="on")
                                .italic.cursor-help(v-html="formatPaymentText(consultation.registration.default_payment.panel.remarks)")
                            div(style="max-width: 30vw")
                              .text-xs {{ consultation.registration.default_payment.panel.remarks }}
                          div(v-if="hasPaymentNote")
                            .text-xxs.font-semibold.mt-2.mb-1 Registration notes:
                            v-tooltip(bottom color="black" content-class="black tooltip-bottom-arrow")
                              template(#activator="{ on, attrs }")
                                div(v-bind="attrs" v-on="on")
                                  .text-xs.cursor-help(v-html="formatPaymentText(consultation.registration.default_payment.note)")
                              div(style="max-width: 30vw")
                                .text-xs {{ consultation.registration.default_payment.note }}
                        //- For url type
                        template(v-else)
                          v-tooltip(v-if="hasPaymentNote" bottom color="black" content-class="black tooltip-bottom-arrow")
                            template(#activator="{ on, attrs }")
                              div(v-bind="attrs" v-on="on")
                                .italic.cursor-help(v-html="formatPaymentText(consultation.registration.default_payment.note)")
                            div(style="max-width: 30vw")
                              .text-xs {{ consultation.registration.default_payment.note }}
                      //- Self pay
                      div(v-else) {{ $t('self_pay') }}
                    div
                      v-btn(@click="showEditPaymentDialog = true" icon small)
                        v-icon(color="primary" size=16) $yzi-edit-05
                v-divider(vertical inset)
                v-col
                  .relative
                    template(v-if="registration.status === 'waiting' || registration.status === 'on_hold'")
                      .text-xs.font-semibold Your patients: {{ consultation.in_queue.total }}&nbsp;
                        span.danger500--text(v-if="consultation.in_queue.urgent > 0") ({{ consultation.in_queue.urgent }} urgent)
                      .text-xxs Total patients in-clinic: {{ consultation.in_queue.all_waiting }} 
                      v-divider.my-2
                      .d-flex.justify-space-between.align-center
                        y-chip.mr-1(:label="`${getStatus(registration)}: ${getWaitingDuration(registration)}`"  size="sm" chipClass="pa-1" outlined)
                          template(#prepend)
                            v-icon.mr-1(size=16) mdi-clock-outline
                        y-chip.red(v-if="registration.urgent" :label="$t('urgent')" size="sm" chipClass="pa-1")
                      v-menu(offset-y bottom rounded="lg" attach z-index="15")
                        template(v-slot:activator="{ on }")
                          v-btn.button-xs.mt-1(color="primary" small depressed block :loading="isLoadingCallPatientIn" :disabled="isLoadingStartConsultation" :dark="Boolean(!isLoadingStartConsultation)" v-on="on") 
                            | {{ $t('call_patient_in') }}
                            v-icon.ml-4(size=20 small) $yzi-chevron-down
                        v-list.py-0
                          template(v-if="seats.length > 0")
                            template(v-for="(seat, seatIdx) in seats")
                              v-divider(v-if="seats !== 0")
                              v-list-item(@click="onCallPatientIn(seat)" :key="'action-' + seatIdx")
                                v-list-item-title.text-sm.basic800--text {{ seat.name }}
                          template(v-else)
                            v-list-item
                              v-list-item-title.text-sm.neutral500--text.italic {{ $t('no_list_available') }}
                    template(v-else-if="registration.status === 'serving'")
                      .d-flex
                        v-row(no-gutters)
                          v-col(cols="12")
                            v-menu(offset-y rounded="lg")
                              template(v-slot:activator="{ on }")
                                v-btn.button-lg.button-padding.mb-2(outlined v-on="on" :class="consultation.in_queue.urgent > 0 ? 'btn-danger' : 'btn-normal neutral200'" @click="onGetPatientList")
                                  .d-flex.align-center
                                    div.text-left.mr-4
                                      .text-xs.font-semibold Your patients: {{ consultation.in_queue.total }}&nbsp;
                                        span.danger500--text(v-if="consultation.in_queue.urgent > 0") ({{ consultation.in_queue.urgent }} urgent)
                                      .text-xxs.font-normal Total patients in-clinic: {{ consultation.in_queue.all_waiting }}
                                    div
                                      v-icon(size=16) mdi-chevron-down
                              v-list.py-0(style="min-width: 450px")
                                template(v-if="patientList.length > 0")
                                  template(v-for="({ patient, urgent, pid, ...item }, patientIdx) in patientList")
                                    v-divider(v-if="patientIdx !== 0")
                                    v-list-item(@click="onViewOnePatient(pid)" :key="'patient-' + patientIdx")
                                      v-list-item-title.text-sm.basic800--text.d-flex.align-center.justify-space-between
                                        .text-sm.font-normal.grow {{ patient.name }}
                                        y-chip.red.mr-2(v-if="urgent" :label="$t('urgent')" size="sm" chipClass="pa-1")
                                        y-chip.mr-2(:label="`${getWaitingDuration(item)}`"  size="sm" chipClass="pa-1" outlined)
                                          template(#prepend)
                                            v-icon.mr-1(size=16) mdi-clock-outline
                                template(v-else)
                                  v-list-item
                                    v-list-item-title.text-sm.neutral500--text.italic {{ $t('no_list_available') }}
                                v-divider
                                v-list-item(@click="onViewAllWaitingPatients")
                                    v-list-item-title.d-flex.justify-center.align-center
                                        .text-sm.underline.font-semibold {{ $t('view_all_waiting_patients') }}
                                        v-icon.ml-1(x-small color="neutral700") mdi-arrow-top-right
                          v-col(cols="12")
                            y-chip.warning100.warning700--text.text-sm.font-semibold(v-if="registration.status === 'serving'" :label="`${getStatus(registration)}: ${getServingDuration(registration)}`" size="sm" chip-class="py-2 px-4" outlined)
                            y-chip.text-sm.font-semibold(v-else :label="$t('on_hold')" size="sm" chip-class="py-2 px-4" outlined)
                    .display-button-position
                      v-btn(v-if="!showRightPanel" @click="toggleRightPanel" icon small color="primary")
                        v-icon(size=22) $yzi-layout-left
            v-divider
            div
              .write-panel
                v-card.round-xl.fill-height.overflow-hidden(outlined)
                  .d-flex.flex-column
                    .neutral300.px-4.py-2
                      v-tooltip(top color="black" content-class="black tooltip-top-arrow" v-if="consultation.case")
                        template(#activator="{ on, attrs }")
                          div(v-bind="attrs" v-on="on")
                            .text-sm.font-semibold.ellipsis-text {{ $t('visit_note') }}: 
                              span.font-normal.cursor-help.neutral700--text {{ consultation.case }}
                        .font-normal.cursor-help {{ consultation.case }}
                      .text-sm.font-semibold(v-else) {{ $t('visit_note') }}: -
                    v-divider
                    div(v-if="isDoctor")
                      template(v-if="registration.status === 'completed' && !isServingDoctor")
                        v-sheet.pa-5.scrollable(:height="textEditorHeight" v-html="consultation.note")
                      text-editor-old(v-else ref="froala" :height="textEditorHeight" :parentForm.sync="consultation.note" :isLoading="isUpdatingNote" :prevRoute="prevRoute" :leadPid="leadPid" :patient="patient.id" @onSave="updateNote(true)" @onFullscreen="handleFullscreen")
                    div(v-else)
                      v-sheet.pa-5.scrollable(:height="textEditorHeight" v-html="consultation.note")
            .py-4.px-8
              .text-md.font-semibold.mb-2 {{ $t('diagnosis') }}
              .d-flex.align-center
                .flex-grow-1
                  diagnosis-field-dropdown.mr-8(:parentDiagnosis="consultation.diagnoses" :leadPid="leadPid" attach :z-index="15" :readOnly="!isDoctor" @updated="onUpdateDiagnosis")
                template(v-if="Auth('component', '/admin/clinic/consultation#set_appointment')")
                  v-icon(color="primary" size=22) $yzi-calendar
                  .text-sm.font-semibold.px-2.underline.mr-4.clickable(@click="onSetAppointment") {{ $t('set_appointment') }}
                v-icon(color="primary" size=22) $yzi-file-04
                .text-sm.font-semibold.px-2.underline.clickable(@click="showAddEditFileDialog = true") {{ $t('upload_file') }}
                  span(v-if="uploadedFileCount > 0")  ({{ uploadedFileCount }})
        
        template(v-show="showRightPanel")
          v-divider(vertical)
          v-col.pa-0(cols=4 v-show="showRightPanel")
            patient-history(:parentPatient="patient" :parentRegistration="registration" :parentActiveMedications="activeMedications" :showRightPanel.sync="showRightPanel" :height="patientHistoryPanelHeight" showToggleRightPanelButton @updated="onPatientHistoryUpdated" @updateActiveMedications="fetchActiveMedication")
      v-divider
      treatment-table.white(:key="treatmentTableKey" :leadPid="leadPid" :patient="patient" :parentTreatments="consultation.treatments" :parentActiveMedications="activeMedications" :parentDefaultPayment="consultation.registration.default_payment" :parentDiagnosis="consultation.diagnoses" :parentIncompletedDocuments="incompletedDocuments" :registrationStatus="consultation.registration.status" @changed="onUpdateTreatmentPlan" @addBulkTreatment="handleUpdateBulkMedication" @updateActiveMedications="fetchActiveMedication")
      div(style="padding-top: 120px; height: 100%;")
      v-footer.white(app inset padless elevation="3" style="z-index: 12")
        .d-flex.flex-column.flex-grow-1
          v-divider
          .d-flex.flex-row.align-center.py-4.px-8.white
            v-spacer
            .mr-8.d-flex.align-center
              .text-xs.font-semibold.neutral500--text.mr-2 {{ $t('total') }}
              .text-md.font-bold {{ totalPrice | displayPrice }}
            template(v-if="isDoctor")
              template(v-if="registration.status === 'waiting' || registration.status === 'on_hold'")
                v-menu(offset-y top rounded="lg" attach z-index="15")
                  template(v-slot:activator="{ on }")
                    v-btn.button-md(color="primary" outlined depressed :loading="isLoadingCallPatientIn" :disabled="isLoadingStartConsultation" :dark="Boolean(!isLoadingStartConsultation)" v-on="on") 
                      | {{ $t('call_patient_in') }}
                      v-icon.ml-2(size=20) $yzi-chevron-down
                  v-list.py-0
                    template(v-if="seats.length > 0")
                      template(v-for="(seat, seatIdx) in seats")
                        v-divider(v-if="seats !== 0")
                        v-list-item(@click="onCallPatientIn(seat)" :key="'action-' + seatIdx")
                          v-list-item-title.text-sm.basic800--text {{ seat.name }}
                    template(v-else)
                      v-list-item
                        v-list-item-title.text-sm.neutral500--text.italic {{ $t('no_list_available') }}
                v-btn.button-md.ml-4(color="primary" depressed :loading="isLoadingStartConsultation" :disabled="isLoadingCallPatientIn" :dark="Boolean(!isLoadingCallPatientIn)" @click="onStartConsultation") {{ registration.status === 'waiting' ? 'Start consultation' : 'Resume consultation' }}
              template(v-else-if="registration.status === 'serving'")
                v-btn.button-md.mr-4(color="primary" depressed outlined :loading="isLoadingPutOnHold" :disabled="isLoadingCompleteConsultation" :dark="Boolean(!isLoadingCompleteConsultation)" @click="onPutOnHold") {{ $t('put_on_hold') }}
                v-btn.button-md(color="primary" depressed :loading="isLoadingCompleteConsultation" :disabled="isLoadingPutOnHold" :dark="Boolean(!isLoadingPutOnHold)" @click="onCompleteConsultation") {{ $t('complete_consultation') }}

  .dialogs
    edit-allergy-dialog(v-if="showEditAllergyDialog" :open.sync="showEditAllergyDialog" :patientId="patient && patient.id" :allergies="consultation.allergies" @updated="onUpdateAllergies")
    edit-payment-dialog(v-if="showEditPaymentDialog" :open.sync="showEditPaymentDialog" :pid="consultation.pid" :payment="consultation.registration.default_payment" :patient="patient" @updated="onUpdatePayment")
    add-advance-appointment(v-if="openAdvAppointmentDialog" :open.sync="openAdvAppointmentDialog" :patient="consultation.patient")
    set-appointment-dialog(v-if="showSetAppointmentDialog" :open.sync="showSetAppointmentDialog" :patient="consultation.patient" @advance="openAdvanceAppointment")
    add-edit-file-dialog(v-if="showAddEditFileDialog" :open.sync="showAddEditFileDialog" :pid="leadPid" @onUpdateCount="updateFileCount")
    incomplete-document-confirmation-dialog(v-if="showIncompleteDocumentConfirmationDialog" :open.sync="showIncompleteDocumentConfirmationDialog" @ignore="onIgnoreIncompleteDocument")
    profile-details-dialog(v-if="showProfileDialog" :open.sync="showProfileDialog" :id="consultation.patient.id")
    consultation-not-found-error-dialog(v-if="showErrorDialog" :open.sync="showErrorDialog")
    interface-update-dialog(v-if="showInterfaceUpdateDialog" :open.sync="showInterfaceUpdateDialog" @learnMore="$emit('showUpgradeDialog')")
</template>

<script>
import { DateTime } from 'luxon'
import { isServerSide } from '@/utils'
import { mapState, mapGetters, mapActions } from 'vuex'
import TreatmentTable from '@/components/clinic/consultation/treatments/TreatmentTable.vue'
import PatientHistory from '@/components/clinic/consultation/patientHistories/PatientHistory.vue'
import DiagnosisFieldDropdown from '@/components/clinic/consultation/diagnosis/DiagnosisFieldDropdown.vue'
import EditAllergyDialog from '@/components/clinic/dialogs/EditAllergyDialog.vue'
import EditPaymentDialog from '@/components/clinic/dialogs/EditPaymentDialog.vue'
import TextEditorOld from '@/components/clinic/consultation/editor/TextEditorOld.vue'
import SetAppointmentDialog from '@/components/clinic/dialogs/SetAppointmentDialog.vue'
import AddAdvanceAppointment from '@/components/clinic/dialogs/AddAdvanceAppointment.vue'
import AddEditFileDialog from '@/components/clinic/dialogs/AddEditFileDialog.vue'
import IncompleteDocumentConfirmationDialog from '@/components/clinic/dialogs/IncompleteDocumentConfirmationDialog.vue'
import ProfileDetailsDialog from '@/components/clinic/dialogs/ProfileDetailsDialog.vue'
import ConsultationNotFoundErrorDialog from '@/components/clinic/dialogs/ConsultationNotFoundErrorDialog.vue'
import InterfaceUpdateDialog from '@/components/clinic/dialogs/InterfaceUpdateDialog.vue'
import bell from '@/assets/audio/bell_announcement.mp3'

const INTERFACE_NOTICE_KEY = 'yz_consultation_interface_notice_last_shown'

export default {
  components: {
    TreatmentTable, PatientHistory, DiagnosisFieldDropdown, EditAllergyDialog, EditPaymentDialog, TextEditorOld,
    SetAppointmentDialog, AddAdvanceAppointment, AddEditFileDialog, IncompleteDocumentConfirmationDialog,
    ProfileDetailsDialog, ConsultationNotFoundErrorDialog, InterfaceUpdateDialog
  },
  data:() => ({
    isFetching: true,
    isLoadingCallPatientIn: false,
    isLoadingPutOnHold: false,
    isLoadingStartConsultation: false,
    isLoadingCompleteConsultation: false,
    isLoadingSeats: true,
    isError: false,
    isUpdatingNote: false,
    showTreatmentPlan: false,
    showRightPanel: true,
    showErrorDialog: false,
    showIncompleteDocumentConfirmationDialog: false,
    showProfileDialog: false,
    showSetAppointmentDialog: false,
    showAddEditFileDialog: false,
    showEditAllergyDialog: false,
    showEditPaymentDialog: false,
    showInterfaceUpdateDialog: false,
    openAdvAppointmentDialog: false,
    consultation: null,
    timer: null,
    statTimer: null,
    currentTime: null,
    incompletedDocumentTrigger: null,
    prevRoute: null,
    treatmentTableKey: 0,
    uploadedFileCount: 0,
    navbarHeight: 72,
    footerHeight: 80,
    stats: {},
    seats: [],
    patientList: [],
    incompletedDocuments: [],
    patientListParams: {
      codes: '1,2',
      status: 'waiting',
      fields: 'pid,patient,urgent,waiting_at',
      ordering: 'urgent'
    },
    reservations: [],
    preorders: [],
    activeMedications: {
      isFetching: false,
      isError: false,
      data: []
    }
  }),
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      isDoctor: 'user/isDoctor',
      patientHistoryHeightAdjustment: 'ui/getConsultationPatientHistoryHeightAdjustment',
      treatmentTableHeightAdjustment: 'ui/getConsultationTreatmentTableHeightAdjustment',
      visitNoteHeightAdjustment: 'ui/getConsultationVisitNoteHeightAdjustment',
      displaySize: 'ui/getDisplaySize'
    }),
    leadPid() {
      return this.$route.params.pid
    },
    patient() {
      return this.consultation.patient
    },
    registration() {
      return this.consultation.registration
    },
    totalPrice() {
      return this.consultation.treatments.reduce((agg, treatment) => {
        agg += treatment.quantity * treatment.price
        return agg
      }, 0)
    },
    getBaseConsultationUrl() {
      if (this.$subdomain) {
        return `${this.$subdomain}.${process.env.VUE_APP_SHORT_URL_DOMAIN}/admin/clinic/consultation`
      } else {
        return `${window.location.origin}/admin/clinic/consultation`
      }
    },
    isPanelRemarkType () {
      if (this.consultation.registration.default_payment.type === 'panel') {
        return this.consultation.registration.default_payment.panel.display_remarks
      }
      return false
    },
    hasPaymentNote () {
      if (this.consultation.registration.default_payment.note && this.consultation.registration.default_payment.note !== '') {
        return true
      }
      return false
    },
    screenHeight () {
      return screen.height
    },
    tableHeight () {
      const tableHeight = this.screenHeight - (this.navbarHeight + this.footerHeight) + this.treatmentTableHeightAdjustment
      return `${tableHeight * 25/100}px`
    },
    patientHistoryPanelHeight () {
      // const screenHeight = screen.height
      // const tableHeight = 30/100*screenHeight + this.patientHistoryHeightAdjustment
      // return `${tableHeight}px`
      switch (this.displaySize) {
        case 'xx-small':
          return '57vh'
        case 'x-small':
          return '51vh'
        case 'small':
          return '46vh'
        default:
          return '41vh'
      }
    },
    textEditorHeight () {
      // const textEditorHeight = this.screenHeight - (this.navbarHeight + this.footerHeight) + this.visitNoteHeightAdjustment
      // return `${textEditorHeight * 15/100}px`
      switch (this.displaySize) {
        case 'xx-small':
          return '38vh'
        case 'x-small':
          return '32vh'
        case 'small':
          return '27vh'
        default:
          return '22vh'
      }
    },
    viewHeight () {
      switch (this.displaySize) {
        case 'xx-small':
          return '139vh'
        case 'x-small':
          return '124vh'
        case 'small':
          return '115vh'
        case 'medium':
          return '101vh'
        case 'large':
          return '90vh'
        case 'x-large':
          return '90vh'
        default:
          return '90vh'
      }
    },
    hasReservationsOrPreorders () {
      return this.reservations.length > 0 || this.preorders.length > 0
    },
    reservationPreorderBannerText () {
      const parts = []
      if (this.reservations.length > 0) {
        parts.push(`${this.reservations.length} reservation${this.reservations.length > 1 ? 's' : ''}`)
      }
      if (this.preorders.length > 0) {
        parts.push(`${this.preorders.length} pre-order${this.preorders.length > 1 ? 's' : ''}`)
      }
      return `This patient has an active ${parts.join(' & ')}. Switch to the new Consultation module to proceed.`
    },
    isServingDoctor () {
      if (!this.isDoctor) {
        return false
      }
      if (this.member.provider === this.consultation.provider) {
        return true
      }
      return false
    }
  },
  watch: {
    stats(val, oldVal) {
      if (oldVal && this.isDoctor) {
        if (val.waiting > oldVal.waiting) {
          this.playAudio();
        }
      }
    },
  },
  async mounted () {
    this.updateTime()
    this.timer = setInterval(this.updateTime, 1000);
    this.statTimer = setInterval(this.fetchStats, 12000);
    try {
      this.isFetching = true
      let allFetching = [this.fetchConsultation(true), this.fetchUploadedFiles(), this.fetchAvailableSeats()]
      await Promise.all([...allFetching])
      this.isFetching = false
      this.checkShowInterfaceUpdate()
      this.fetchReservationsAndPreorders()
    } catch (error) {
      this.isError = true
      const { config, status } = error.response
      const consultationEndpoint = config.url.includes('/consultations')
      if (consultationEndpoint && status === 404) {
        this.showErrorDialog = true
        return
      }
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
    }
  },
  beforeDestroy () {
    this.clearTimer()
  },
  // beforeRouteEnter(to, from, next) {
  //   next(vm => {
  //     if (vm.propAttrs.prevRoute) {
  //       vm.prevRoute = vm.$attrs.prevRoute
  //     } else {
  //       vm.prevRoute = from
  //     }
  //   })
  // },
  // async beforeRouteLeave (to, from, next) {
  //   if (!this.isError) {
  //     await this.updateNote(false)
  //   }
  //   next()
  // },
  methods: {
    ...mapActions(['showMessage']),
    fetchConsultation (getActiveMedication = false) {
      return this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}`).then(({ data }) => {
        this.consultation = data
        if (getActiveMedication) {
          this.fetchActiveMedication()
        }
      })
    },
    fetchUploadedFiles () {
      this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}/attachments`, { page_size: 1 })
        .then(resp => {
          this.uploadedFileCount = resp.data.count
        })
    },
    fetchAvailableSeats () {
      this.isLoadingCallPatientIn = true
      let params = {
        status: 'active',
        type: 'room',
        fields: 'id,name',
        page_size: -1
      }
      return this.$axios.get(`/clinics/${this.company.slug}/facilities`, { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.seats = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoadingCallPatientIn = false
        })
    },
    fetchStats() {
      this.$axios.get(`/clinics/${this.company.slug}/registrations/stats`, { params: { codes: "1,2" } })
        .then((resp) => {
          if (resp.data && resp.data.stats) {
            this.stats = resp.data.stats
          }
        })
        .catch(() => {});
    },
    toggleTreatmentPlan () {
      this.showTreatmentPlan = !this.showTreatmentPlan
    },
    toggleRightPanel () {
      this.showRightPanel = !this.showRightPanel
    },
    async onClickBack () {
      let route = { name: 'ClinicConsultation' }
      if (isServerSide()) {
        if (this.isDoctor) { route = { name: 'ClinicConsultation' } }
        else { route = { name: 'ClinicRegistration' } }
      }
      if (this.$route.params && this.$route.params.origin) {
         route = { name: this.$route.params.origin }
      }
      if (this.$route.params && this.$route.params.data && Object.keys(this.$route.params.data).length) {
        route = { ...route, ...this.$route.params.data }
      }
      return this.$router.push(route).catch(() => {})
    },
    onIgnoreIncompleteDocument () {
      this.incompletedDocuments = []
      switch (this.incompletedDocumentTrigger) {
        case 'backButton':
          this.onClickBack()
          break

        case 'completeConsultation':
          this.onCompleteConsultation()
          break
      
        default:
          break
      }
    },
    updateFileCount (attachments) {
      this.uploadedFileCount = attachments.length
    },
    openProfileWindow() {
      window.open(`/admin/clinic/patient/${this.patient.id}`, '_blank')
    },
    updateTime() {
      this.currentTime = new Date().toISOString()
    },
    getStatus(item) {
      const text = item.status.replace(/_/g, ' ')
      return this.$options.filters.capitalize(text)
    },
    getWaitingDuration(item) {
      const end = DateTime.fromISO(this.currentTime)
      const start = DateTime.fromISO(item.waiting_at)

      let diff = end.diff(start, ['hours', 'minutes']);
      let hours = Math.floor(diff.toObject().hours)
      let minutes = Math.floor(diff.toObject().minutes)
      if (hours) {
        return `${hours} ${this.$options.filters.pluralize('hour', { count:hours })}  ${minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
      }
      return `${minutes < 1 ? '<1' : minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
    },
    getServingDuration(item) {
      const end = DateTime.fromISO(this.currentTime)
      const start = DateTime.fromISO(item.serving_at)

      let diff = end.diff(start, ['hours', 'minutes', 'seconds']);
      let hours = Math.floor(diff.toObject().hours)
      let minutes = Math.floor(diff.toObject().minutes)
      let seconds = Math.floor(diff.toObject().seconds)
      if (seconds <= 0) {
        return '00:00'
      }
      let hoursText = hours < 10 ? `0${hours}` : `${hours}`
      let minutesText = minutes < 10 ? `0${minutes}` : `${minutes}`
      let secondsText = seconds < 10 ? `0${seconds}` : `${seconds}`
      if (hours) {
        return `${hoursText}:${minutesText}:${secondsText}` 
      }
      return `${minutesText}:${secondsText}`
    },
    clearTimer() {
      clearInterval(this.timer)
      clearInterval(this.statTimer)
    },
    onViewOnePatient(leadPid) {
      const url = `${this.getBaseConsultationUrl}/${leadPid}`
      window.open(url)
    },
    onViewAllWaitingPatients() {
      const url = `${this.getBaseConsultationUrl}?tab=waiting`
      window.open(url)
    },
    async onGetPatientList() {
      try {
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/registrations`, { params: this.patientListParams })
        this.patientList = data.results
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    onCallPatientIn(seat) {
      this.isLoadingCallPatientIn = true
      let payload = {
        seat: seat?.id,
        status: 'serving'
      }
      if (this.member.roles.includes('Agent')) {
        payload.provider = this.member.provider
      }
      this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, payload)
        .then(() => {
          this.updateNote()
        })
        .catch(error => {
          const errorData = error.response?.data?.detail
          if (errorData && errorData.includes('currently occupied')) {
            this.showMessage(['error', `${seat.name} is currently occupied`])
          } else {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
        .finally(() => {
          this.isLoadingCallPatientIn = false
        })
    },
    onStartConsultation () {
      this.isLoadingStartConsultation = true
      let payload = {
        status: 'serving'
      }
      if (this.member.roles.includes('Agent')) {
        payload.provider = this.member.provider
      }
      this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, payload)
        .then(() => {
          this.updateNote()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoadingStartConsultation = false
        })
    },
    onPutOnHold() {
      this.isLoadingPutOnHold = true
      this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, { status: 'on_hold' })
        .then(() => {
          this.updateNote()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoadingPutOnHold = false
        })
    },
    async onCompleteConsultation() {
      this.isLoadingCompleteConsultation = true
      try {
        await this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, { status: 'checkout' })
        const itemCount = this.consultation.treatments?.length || 0
        const totalAmount = this.$options.filters.displayPrice(this.totalPrice, this.$currency)
        this.createActivityLog('complete_consultation', `Consultation completed with ${itemCount} items worth ${totalAmount}`)
        await this.updateNote()
        // this.$router.push('/admin/clinic/consultation?tab=waiting').catch(() => {})
        let route = { name: 'ClinicConsultation'}
        if (this.$route.params && this.$route.params.data && Object.keys(this.$route.params.data).length) {
          route = { ...route, ...this.$route.params.data }
        }
        this.$router.push(route)
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingCompleteConsultation = false
      }
    },
    async onPatientHistoryUpdated() {
      try {
        await this.updateNote()
        await this.fetchConsultation()
        this.treatmentTableKey++
        this.showMessage(['success', this.$t('success_message.medication_added_successfully')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    async handleUpdateBulkMedication () {
      try {
        await this.fetchConsultation()
      // eslint-disable-next-line no-empty
      } catch (error) {}
    },
    onUpdateAllergies(allergies) {
      this.consultation.allergies = allergies
    },
    onUpdatePayment(payment) {
      this.consultation.registration.default_payment = payment
    },
    onUpdateDiagnosis(diagnoses) {
      this.consultation.diagnoses = diagnoses
    },
    updateNote (showMessage = false) {
      let payload = {}
      if (this.isDoctor && this.consultation.note !== null) {
        payload.note = this.consultation.note
      }
      this.isUpdatingNote = true
      const endpoint = `/clinics/${this.company.slug}/consultations/${this.leadPid}`
      return this.$axios.patch(endpoint, payload)
        .then((resp) => {
          this.consultation = resp.data
          if (showMessage) {
            this.showMessage(['success', this.$t('success_message.update_successful')])
          }
          this.updateConsultationNoteLog(endpoint, payload, resp, 'PATCH')
        })
        .catch(() => {
          if (showMessage) {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
        .finally(() => {
          this.isUpdatingNote = false
        })
    },
    async handleFullscreen(resolve) {
      try {
        await this.updateNote(false)
        resolve()
      } catch (error) {
        this.showMessage(['error', 'Save failed'])
        console.error('Save failed:', error)
        resolve()
      }
    },
    onSetAppointment () {
      this.showSetAppointmentDialog = true
    },
    formatPaymentText (text) {
      if (text.length > 35) {
        return this.$options.filters.truncate(text, 35)
      }
      return text
    },
    async onUpdateTreatmentPlan () {
      try {
        await this.updateNote(false)
        await this.fetchConsultation()
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    openAdvanceAppointment () {
      this.showSetAppointmentDialog = false
      this.openAdvAppointmentDialog = true
    },
    updateConsultationNoteLog (endpoint, payload, response, method) {
      let data = {
        company: this.company.id,
        object_id: null,
        object_ref: this.leadPid,
        activity: `Update consultation note (${this.leadPid})`,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    async fetchActiveMedication () {
      this.activeMedications.isFetching = true
      const params = {
        page_size: -1,
        patient: this.patient.id,
        expand: 'medication'
      }
      return this.$axios.get(`/clinics/${this.company.slug}/active_medications`, { params })
        .then(({ data }) => {
          if (data?.results?.length > 0) {
            this.activeMedications.data = data.results
          } else {
            this.activeMedications.data = []
          }
        })
        .catch(() => {
          this.activeMedications.isError = true
        })
        .finally(() => {
          this.activeMedications.isFetching = false
        })
    },
    playAudio() {
      this.audio = new Audio(bell)
      this.audio.play()
    },
    async fetchReservationsAndPreorders () {
      try {
        const [reservationsResp, preordersResp] = await Promise.all([
          this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params: { patient: this.consultation.patient.id, status: 'active', page_size: -1 } }),
          this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params: { patient: this.consultation.patient.id, status: 'active', page_size: -1 } })
        ])
        if (reservationsResp.data?.results) this.reservations = reservationsResp.data.results
        if (preordersResp.data?.results) this.preorders = preordersResp.data.results
      } catch {
        // silently ignore
      }
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: 'consultation',
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    },
    checkShowInterfaceUpdate () {
      const today = DateTime.local().toFormat('yyyy-LL-dd')
      if (localStorage.getItem(INTERFACE_NOTICE_KEY) === today) return
      this.showInterfaceUpdateDialog = true
      localStorage.setItem(INTERFACE_NOTICE_KEY, today)
    },
  },
}
</script>

<style lang="scss" scoped>
.main {
  position: absolute;
  top: 0;
  display: flex;
  flex-direction: column;
  width: 100% !important;
  height: var(--view-height) !important;
  overflow: auto;
}
// .middle-content {
//   padding-bottom: 340px;
// }
.h-100 {
  height: 100% !important;
}
.write-panel {
  height: 100% !important;
  padding: 16px 36px 0px 36px;
}
.overflow-hidden {
  overflow: hidden !important;
}
.overflow {
  overflow: scroll;
}
.btn-normal {
  border: 1px solid #EEEEEE !important;
}
.btn-danger {
  border: none !important;
  background: #FFEBEB !important;
}
.number-padding {
  padding-left: 10px !important;
  padding-right: 10px !important;
}
.ellipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}
.profile-height {
  height: 160px;
}
.relative {
  position: relative;
}
.display-button-position {
  position: absolute;
  top: 0;
  right: 0;
}
.scrollable {
  overflow-y: auto;
}
.hover-text:hover {
  border-radius: 5px;
  display: inline-block;
  background-color: var(--v-neutral300);
}
</style>